import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { User, Download, Search, Mail, Phone, Calendar, Loader2, Trash2, CheckSquare, Activity, MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import DashboardPageLayout from './DashboardPageLayout';
import { Card, CardContent } from './ui/card';
import CardListViewToggle from './CardListViewToggle';
import { Badge } from './ui/badge';
import ActionBar from './ActionBar';
import { PrimaryButton, SecondaryButton, DangerButton } from './Buttons';
import MassActionToolbar from './MassActionToolbar';
import EmptyState from './EmptyState';
import StandardModal from './StandardModal';
import InfoCard from './InfoCard';
import Pagination from './Pagination';
import ResourceCard from './ResourceCard';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const CustomersTab = () => {
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [exporting, setExporting] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // Default to list view
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [massActionMode, setMassActionMode] = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [expandedCustomers, setExpandedCustomers] = useState({});
  
  // SMS functionality
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [selectedCustomerForSms, setSelectedCustomerForSms] = useState(null);
  const [smsMessage, setSmsMessage] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  
  // Sorting and filtering
  const [sortBy, setSortBy] = useState('recent'); // recent, name, bookings, revenue
  const [sortDirection, setSortDirection] = useState('desc');
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      
      // Fetch all bookings (includes waivers data)
      const bookingsRes = await api.get('/bookings');
      const allBookings = bookingsRes.data || [];
      
      // Aggregate by email/phone
      const customerMap = {};
      
      // Add booking customers
      allBookings.forEach(booking => {
        const email = booking.customerEmail?.trim();
        if (!email) return;
        
        const key = email.toLowerCase();
        
        if (!customerMap[key]) {
          customerMap[key] = {
            name: (booking.customerName || 'Unknown').trim(),
            email: email,
            phone: booking.customerPhone?.trim() || 'N/A',
            bookings: [],
            waiverSigns: 0,
            totalSpent: 0,
            lastBooking: null,
            source: 'booking',
            firstSeen: null
          };
        }
        
        customerMap[key].bookings.push(booking);
        
        // Calculate total spent from various price fields
        const price = parseFloat(booking.totalAmount || booking.amountPaid || booking.servicePrice || 0);
        customerMap[key].totalSpent += price;
        
        // Track last booking date
        const bookingDate = new Date(booking.createdAt || booking.preferredDate || booking.date);
        if (!customerMap[key].lastBooking || bookingDate > new Date(customerMap[key].lastBooking)) {
          customerMap[key].lastBooking = booking.createdAt || booking.preferredDate || booking.date;
        }
        
        // Track first seen date
        if (!customerMap[key].firstSeen || bookingDate < new Date(customerMap[key].firstSeen)) {
          customerMap[key].firstSeen = booking.createdAt || booking.preferredDate || booking.date;
        }
        
        // Update phone if we didn't have it
        if (customerMap[key].phone === 'N/A' && booking.customerPhone) {
          customerMap[key].phone = booking.customerPhone.trim();
        }
      });
      
      // Add waiver signers from bookings
      allBookings.forEach(booking => {
        if (booking.waiverSignatures && booking.waiverSignatures.length > 0) {
          booking.waiverSignatures.forEach(sig => {
            const phone = sig.phoneNumber?.trim();
            const name = sig.signatureName?.trim();
            
            if (!phone || !name) return;
            
            // Check if this person already exists by email or phone
            let existing = null;
            
            // First try to find by email
            if (sig.email) {
              const emailKey = sig.email.toLowerCase();
              existing = customerMap[emailKey];
            }
            
            // If not found by email, try by phone
            if (!existing) {
              existing = Object.values(customerMap).find(c => c.phone === phone);
            }
            
            if (existing) {
              // Increment waiver count for existing customer
              existing.waiverSigns = (existing.waiverSigns || 0) + 1;
              existing.source = 'both';
            } else {
              // New waiver-only contact
              const key = `waiver_${phone}`;
              customerMap[key] = {
                name: name,
                email: 'N/A',
                phone: phone,
                bookings: [],
                waiverSigns: 1,
                totalSpent: 0,
                lastBooking: sig.signedAt || booking.date,
                firstSeen: sig.signedAt || booking.date,
                source: 'waiver'
              };
            }
          });
        }
      });
      
      // Convert to array and sort by most recent activity
      const customerArray = Object.values(customerMap).sort((a, b) => {
        const dateA = new Date(a.lastBooking || 0);
        const dateB = new Date(b.lastBooking || 0);
        return dateB - dateA;
      });
      
      setCustomers(customerArray);
      
      // Show success toast only if we have data
      if (customerArray.length > 0) {
        toast.success(`Loaded ${customerArray.length} contacts`);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      
      // Better error messages
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log out and log back in.');
      } else if (error.response?.status === 403) {
        toast.error('You don\'t have permission to view relations.');
      } else {
        toast.error('Failed to load relations. Please try again.');
      }
      
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    setExporting(true);
    
    try {
      // Create CSV content from sorted and filtered customers
      const headers = ['Name', 'Email', 'Phone', 'Source', 'Bookings', 'Waivers Signed', 'Total Spent', 'Last Activity'];
      const rows = sortedCustomers.map(customer => [
        customer.name,
        customer.email,
        customer.phone,
        customer.source,
        customer.bookings.length,
        customer.waiverSigns || 0,
        `$${customer.totalSpent.toFixed(2)}`,
        new Date(customer.lastBooking).toLocaleDateString()
      ]);
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');
      
      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relations-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      
      toast.success(`Exported ${sortedCustomers.length} contacts!`);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Mass action handlers
  const toggleMassActionMode = () => {
    setMassActionMode(!massActionMode);
    setSelectedCustomers([]);
  };

  const toggleCustomerSelection = (customerIndex) => {
    setSelectedCustomers(prev => 
      prev.includes(customerIndex) 
        ? prev.filter(id => id !== customerIndex)
        : [...prev, customerIndex]
    );
  };

  const selectAllCustomers = () => {
    setSelectedCustomers(paginatedCustomers.map((_, idx) => idx));
  };

  const deselectAllCustomers = () => {
    setSelectedCustomers([]);
  };

  const handleBulkDelete = async () => {
    // This would require a backend endpoint to delete customers
    toast.info('Bulk delete functionality coming soon');
    setSelectedCustomers([]);
    setMassActionMode(false);
    setBulkDeleteConfirm(false);
  };

  const handleSendSms = (customer) => {
    setSelectedCustomerForSms(customer);
    
    // Pre-fill with booking confirmation message if they have bookings
    let defaultMessage = '';
    if (customer.bookings.length > 0) {
      const lastBooking = customer.bookings[0];
      const bookingDate = new Date(lastBooking.preferredDate || lastBooking.date).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
      defaultMessage = `🎉 Hi ${customer.name.split(' ')[0]}! Here's your booking confirmation. View your ticket: ${window.location.origin}/customer-ticket/${lastBooking.confirmationCode}`;
    } else {
      defaultMessage = `Hi ${customer.name.split(' ')[0]}, thank you for choosing us!`;
    }
    
    setSmsMessage(defaultMessage);
    setSmsModalOpen(true);
  };

  const sendSmsMessage = async () => {
    if (!smsMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    if (selectedCustomerForSms.phone === 'N/A') {
      toast.error('This customer doesn\'t have a phone number');
      return;
    }

    try {
      setSendingSms(true);
      
      // Format phone number - add +1 if missing (same as waiver)
      const phone = selectedCustomerForSms.phone;
      const formattedPhone = phone.startsWith('+') ? phone : `+1${phone}`;
      
      const response = await api.post('/sms/send-custom', {
        phone: formattedPhone,
        message: smsMessage.trim(),
        customerName: selectedCustomerForSms.name
      });
      
      toast.success(`SMS sent to ${selectedCustomerForSms.name}!`);
      setSmsModalOpen(false);
      setSmsMessage('');
      setSelectedCustomerForSms(null);
    } catch (error) {
      console.error('Error sending SMS:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to send SMS';
      toast.error(`SMS Error: ${errorMessage}`);
    } finally {
      setSendingSms(false);
    }
  };

  const filteredCustomers = customers.filter(customer => {
    const query = searchQuery.toLowerCase();
    const matchesSearch = (
      customer.name.toLowerCase().includes(query) ||
      customer.email.toLowerCase().includes(query) ||
      customer.phone.includes(query)
    );
    
    if (!matchesSearch) return false;
    
    // Date filter
    if (dateFilter !== 'all') {
      const lastBookingDate = new Date(customer.lastBooking);
      const today = new Date();
      
      if (dateFilter === 'today') {
        const todayStr = today.toISOString().split('T')[0];
        const bookingStr = lastBookingDate.toISOString().split('T')[0];
        if (todayStr !== bookingStr) return false;
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        if (lastBookingDate < weekAgo) return false;
      } else if (dateFilter === 'month') {
        const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        if (lastBookingDate < monthAgo) return false;
      } else if (dateFilter === 'custom' && customStartDate && customEndDate) {
        const bookingStr = lastBookingDate.toISOString().split('T')[0];
        if (bookingStr < customStartDate || bookingStr > customEndDate) return false;
      }
    }
    
    return true;
  });

  // Sort customers
  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'recent':
        comparison = new Date(b.lastBooking) - new Date(a.lastBooking);
        break;
      case 'bookings':
        comparison = b.bookings.length - a.bookings.length;
        break;
      case 'revenue':
        comparison = b.totalSpent - a.totalSpent;
        break;
      default:
        comparison = 0;
    }
    
    return sortDirection === 'asc' ? -comparison : comparison;
  });

  // Pagination
  const totalPages = Math.ceil(sortedCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCustomers = sortedCustomers.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <DashboardPageLayout
      title="Relations"
      subtitle="Your complete contact database - customers and waiver signers"
      actions={
        <ActionBar>
          <Button
            onClick={toggleMassActionMode}
            variant={massActionMode ? "default" : "outline"}
            size="sm"
            style={massActionMode ? { background: '#007AFF', color: 'white' } : {}}
            className="text-xs sm:text-sm h-9"
          >
            <CheckSquare className="w-4 h-4 mr-1 sm:mr-2" />
            Mass Actions
          </Button>
          
          <CardListViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
          
          <PrimaryButton 
            onClick={handleExport}
            disabled={exporting || sortedCustomers.length === 0}
            className="text-xs sm:text-sm h-9"
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 sm:mr-2 animate-spin" />
                <span className="hidden xs:inline">Exporting...</span>
                <span className="xs:hidden">...</span>
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden xs:inline">Export CSV</span>
                <span className="xs:hidden">Export</span>
              </>
            )}
          </PrimaryButton>
        </ActionBar>
      }
    >
      <div className="space-y-6">
        {/* Mass Action Toolbar */}
        {massActionMode && (
          <MassActionToolbar
            selectedCount={selectedCustomers.length}
            onSelectAll={selectAllCustomers}
            onDeselectAll={deselectAllCustomers}
            onDelete={() => setBulkDeleteConfirm(true)}
            itemName="contact"
          />
        )}

        {/* Filters & Sorting */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Sort & Filter Row */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t">
              {/* Sort By */}
              <div className="flex items-center gap-2 flex-1">
                <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Sort By:</span>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { key: 'recent', label: 'Recent Activity' },
                    { key: 'name', label: 'Name' },
                    { key: 'bookings', label: 'Most Bookings' },
                    { key: 'revenue', label: 'Highest Revenue' }
                  ].map(sort => (
                    <button
                      key={sort.key}
                      onClick={() => {
                        if (sortBy === sort.key) {
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortBy(sort.key);
                          setSortDirection(sort.key === 'name' ? 'asc' : 'desc');
                        }
                      }}
                      className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors flex items-center gap-1 ${
                        sortBy === sort.key
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {sort.label}
                      {sortBy === sort.key && (
                        <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Date Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Activity:</span>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { key: 'all', label: 'All Time' },
                    { key: 'today', label: 'Today' },
                    { key: 'week', label: 'This Week' },
                    { key: 'month', label: 'This Month' }
                  ].map(filter => (
                    <button
                      key={filter.key}
                      onClick={() => setDateFilter(filter.key)}
                      className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                        dateFilter === filter.key
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Results Count */}
            {(searchQuery || dateFilter !== 'all') && (
              <p className="text-sm text-gray-600">
                Showing {sortedCustomers.length} of {customers.length} contacts
              </p>
            )}
          </CardContent>
        </Card>

        {/* Contacts Display */}
        {sortedCustomers.length === 0 ? (
          <EmptyState
            icon={User}
            title={searchQuery || dateFilter !== 'all' ? 'No contacts found' : 'No contacts yet'}
            description={searchQuery || dateFilter !== 'all' ? 'Try adjusting your filters or search terms' : 'Contacts will appear here after their first booking or waiver'}
            actionLabel={searchQuery || dateFilter !== 'all' ? 'Clear Filters' : null}
            onAction={searchQuery || dateFilter !== 'all' ? () => {
              setSearchQuery('');
              setDateFilter('all');
            } : null}
          />
        ) : viewMode === 'card' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedCustomers.map((customer, idx) => {
              const globalIdx = startIndex + idx;
              const isSelected = selectedCustomers.includes(idx);
              
              // Source badge configuration
              const sourceBadge = customer.source === 'waiver' ? {
                label: 'Waiver Only',
                color: '#9333EA',
                variant: 'outline'
              } : customer.source === 'both' ? {
                label: 'Booking + Waiver',
                color: '#059669',
                variant: 'outline'
              } : {
                label: 'Booking',
                color: '#0EA5E9',
                variant: 'outline'
              };

              // Build expanded content
              const expandedContent = (
                <>
                  {/* Stats Section */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <div className="text-lg font-bold text-blue-900">{customer.bookings.length}</div>
                      <div className="text-[10px] text-blue-700 uppercase">Bookings</div>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-lg">
                      <div className="text-lg font-bold text-purple-900">{customer.waiverSigns || 0}</div>
                      <div className="text-[10px] text-purple-700 uppercase">Waivers</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="text-lg font-bold text-green-900">${customer.totalSpent.toFixed(0)}</div>
                      <div className="text-[10px] text-green-700 uppercase">Revenue</div>
                    </div>
                  </div>

                  {/* Recent Bookings */}
                  {customer.bookings.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-2">Recent Activity</p>
                      <div className="space-y-1">
                        {customer.bookings.slice(0, 3).map((booking, i) => (
                          <div key={i} className="flex justify-between text-xs text-gray-600 bg-gray-50 p-2 rounded">
                            <span className="truncate mr-2">{booking.serviceName || 'Service'}</span>
                            <span className="text-gray-500 flex-shrink-0">
                              {new Date(booking.preferredDate || booking.date).toLocaleDateString()}
                            </span>
                          </div>
                        ))}
                        {customer.bookings.length > 3 && (
                          <p className="text-xs text-gray-500 italic text-center pt-1">
                            +{customer.bookings.length - 3} more bookings
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              );

              return (
                <ResourceCard
                  key={globalIdx}
                  id={globalIdx}
                  name={customer.name}
                  icon={User}
                  iconColor="#007AFF"
                  primaryBadge={sourceBadge}
                  details={[
                    ...(customer.email !== 'N/A' ? [{ icon: <Mail className="w-3 h-3" />, text: customer.email }] : []),
                    ...(customer.phone !== 'N/A' ? [{ icon: <Phone className="w-3 h-3" />, text: customer.phone }] : []),
                    { icon: <Calendar className="w-3 h-3" />, text: `Last: ${new Date(customer.lastBooking).toLocaleDateString()}` }
                  ]}
                  additionalBadges={
                    <div className="flex gap-1 mt-2 flex-wrap">
                      <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-xs">
                        {customer.bookings.length} {customer.bookings.length === 1 ? 'booking' : 'bookings'}
                      </Badge>
                      {customer.waiverSigns > 0 && (
                        <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 text-xs">
                          {customer.waiverSigns} {customer.waiverSigns === 1 ? 'waiver' : 'waivers'}
                        </Badge>
                      )}
                      <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-xs">
                        ${customer.totalSpent.toFixed(2)}
                      </Badge>
                    </div>
                  }
                  customActions={null}
                  showSeeMore={true}
                  expandedContent={expandedContent}
                  isExpanded={expandedCustomers[globalIdx]}
                  onToggleExpand={() => {
                    setExpandedCustomers(prev => ({
                      ...prev,
                      [globalIdx]: !prev[globalIdx]
                    }));
                  }}
                  leftBorderColors={['#007AFF']}
                  massActionMode={massActionMode}
                  isSelected={isSelected}
                  onToggleSelect={() => toggleCustomerSelection(idx)}
                  hideEditDelete={true}
                />
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {massActionMode && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={selectedCustomers.length === paginatedCustomers.length && paginatedCustomers.length > 0}
                          onChange={(e) => e.target.checked ? selectAllCustomers() : deselectAllCustomers()}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </th>
                    )}
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Activity
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Revenue
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedCustomers.map((customer, idx) => {
                    const isSelected = selectedCustomers.includes(idx);
                    return (
                      <tr key={idx} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                        {massActionMode && (
                          <td className="px-4 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleCustomerSelection(idx)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        )}
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#007AFF20' }}>
                              <User className="w-5 h-5" style={{ color: '#007AFF' }} />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{customer.name}</div>
                              <div className="text-xs text-gray-500">
                                Last: {new Date(customer.lastBooking).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4">
                          <div className="text-sm text-gray-600">
                            {customer.email !== 'N/A' && (
                              <div className="flex items-center gap-1 mb-1">
                                <Mail className="w-3 h-3" />
                                <span className="truncate max-w-[150px]">{customer.email}</span>
                              </div>
                            )}
                            {customer.phone !== 'N/A' && (
                              <div className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                <span>{customer.phone}</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <Badge 
                            className={`text-xs ${
                              customer.source === 'waiver' ? 'bg-purple-100 text-purple-700' :
                              customer.source === 'both' ? 'bg-green-100 text-green-700' :
                              'bg-blue-100 text-blue-700'
                            }`}
                          >
                            {customer.source === 'waiver' ? 'Waiver Only' :
                             customer.source === 'both' ? 'Both' : 'Booking'}
                          </Badge>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="text-center">
                              <div className="font-semibold text-blue-600">{customer.bookings.length}</div>
                              <div className="text-[10px] text-gray-500">Bookings</div>
                            </div>
                            {customer.waiverSigns > 0 && (
                              <>
                                <span className="text-gray-300">|</span>
                                <div className="text-center">
                                  <div className="font-semibold text-purple-600">{customer.waiverSigns}</div>
                                  <div className="text-[10px] text-gray-500">Waivers</div>
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-green-600">
                            ${customer.totalSpent.toFixed(2)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {sortedCustomers.length > itemsPerPage && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={sortedCustomers.length}
          />
        )}
        {/* SMS Modal */}
        <Dialog open={smsModalOpen} onOpenChange={setSmsModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Send SMS to {selectedCustomerForSms?.name}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                  <Phone className="w-4 h-4" />
                  <span>{selectedCustomerForSms?.phone}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <Textarea
                  value={smsMessage}
                  onChange={(e) => setSmsMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="min-h-[120px] resize-none"
                  maxLength={160}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {smsMessage.length}/160 characters
                </p>
              </div>

              {/* Quick templates */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Quick Templates:</p>
                <div className="space-y-1">
                  {selectedCustomerForSms?.bookings.length > 0 && (
                    <button
                      onClick={() => {
                        const lastBooking = selectedCustomerForSms.bookings[0];
                        const bookingDate = new Date(lastBooking.preferredDate || lastBooking.date).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric' 
                        });
                        setSmsMessage(`🎉 Hi ${selectedCustomerForSms.name.split(' ')[0]}! Here's your booking ticket: ${window.location.origin}/customer-ticket/${lastBooking.confirmationCode}`);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2 py-1 rounded"
                    >
                      📱 Send Ticket Link
                    </button>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  💡 SMS will be sent from your Twilio number. Customer can reply directly.
                </p>
              </div>

              <div className="flex flex-col md:flex-row justify-end gap-2 pt-4 border-t">
                <SecondaryButton 
                  onClick={() => setSmsModalOpen(false)} 
                  className="w-full md:w-auto"
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton 
                  onClick={sendSmsMessage}
                  disabled={sendingSms || !smsMessage.trim()}
                  className="w-full md:w-auto bg-green-600 hover:bg-green-700"
                >
                  {sendingSms ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send SMS
                    </>
                  )}
                </PrimaryButton>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Modal */}
        <StandardModal
          isOpen={bulkDeleteConfirm}
          onClose={() => setBulkDeleteConfirm(false)}
          title="Delete Multiple Contacts?"
          description={`Are you sure you want to delete ${selectedCustomers.length} contact(s)?`}
          size="sm"
          footer={
            <>
              <SecondaryButton
                onClick={() => setBulkDeleteConfirm(false)}
                className="flex-1"
              >
                Cancel
              </SecondaryButton>
              <DangerButton
                onClick={handleBulkDelete}
                className="flex-1"
              >
                Delete {selectedCustomers.length} Contacts
              </DangerButton>
            </>
          }
        >
          <InfoCard variant="danger">
            <p className="font-medium mb-1">This action cannot be undone.</p>
            <p>All selected contacts will be permanently deleted.</p>
          </InfoCard>
        </StandardModal>
      </div>
    </DashboardPageLayout>
  );
};

export default CustomersTab;
